/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useRef, useEffect } from "react";
import CustomButton from "../UI/Button";
import styled from "styled-components";
import {
  TextField,
  Typography,
  MenuItem,
  Box,
  Grid,
  FormControlLabel,
  Switch,
} from "@material-ui/core";
import { device } from "../../Utils";
import DatePicker from "../UI/DatePicker";
import TimePicker from "../UI/TimePicker";
import moment from "moment";
import {
  Autocomplete,
  DirectionsRenderer,
  GoogleMap,
  useLoadScript,
} from "@react-google-maps/api";
import CircularLoader from "../UI/Loader";
import { COLORS } from "../../assets/colors";
import { Alert } from "@material-ui/lab";
import configMerged from "../../config";
import { FaUpload } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const libraries = ["places"];

function Publish({
  ridesSubmitFrom,
  isLoading,
  submitting,
  currentUser,
  licenseStatus,
  checkLicense,
}) {
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY,
    libraries,
  });
  const center = { lat: 31.582045, lng: 74.329376 };
  const [map, setMap] = useState(/** @type google.types.Map*/ null);
  const [directionResponse, setDirectionResponse] = useState(null);
  const [timeError, setTimeError] = useState("");
  const [dateError, setDateError] = useState("");

  /** @type React.mutableRefObject<HTMLInputElement>*/
  const originRef = useRef();
  /** @type React.mutableRefObject<HTMLInputElement>*/
  const destinationRef = useRef();
  /** @type React.mutableRefObject<HTMLInputElement>*/
  const startCityRef = useRef();

  const navigate = useNavigate();

  const queryParams = new URLSearchParams(window.location.search);
  const paramLeavingFrom = queryParams.get("leavingFrom") || "";
  const paramGoingTo = queryParams.get("goingTo") || "";
  const paramWishId = queryParams.get("wishId") || "";

  const [state, setState] = useState({
    leavingFrom: paramLeavingFrom,
    headingTo: paramGoingTo,
    pickupLocation: paramLeavingFrom,
    dropLocation: paramGoingTo,
    wishId: paramWishId,
    date: null,
    time: null,
    arrivalDate: null,
    arrivalTime: null,
    vehicleName: "",
    vehicleMdel: "",
    vehicleType: "car",
    route: "",
    ticket: "",
    totalSeats: 4,
    differentSeatPricing: false,
    frontSeatPrice: "",
    backSeatPrice: "",
    frontSeatsTotal: 1,
    error: false,
    duration: "",
    distance: "",
    start_lat: null,
    start_lng: null,
    end_lat: null,
    end_lng: null,
    smoking: false,
    automatic_approval: false,
    female_only: false,
  });
  const [invalidTicket, setInvalidTicket] = useState(false);

  useEffect(() => {
    if (currentUser?.gender == "female") {
      setState((prevState) => ({
        ...prevState,
        female_only: true,
      }));
    }
  }, [currentUser?.gender]);

  useEffect(() => {
    if (isLoaded && paramLeavingFrom && paramGoingTo) {
      const calculateInitialRoute = async () => {
        const directionService = new google.maps.DirectionsService();
        try {
          const results = await directionService.route({
            origin: paramLeavingFrom,
            destination: paramGoingTo,
            travelMode: google.maps.TravelMode.DRIVING,
          });

          const startLocationLatLng = {
            lat: results.routes[0].legs[0].start_location.lat(),
            lng: results.routes[0].legs[0].start_location.lng(),
          };
          const endLocationLatLng = {
            lat: results.routes[0].legs[0].end_location.lat(),
            lng: results.routes[0].legs[0].end_location.lng(),
          };
          const route = results.routes[0].summary;

          setDirectionResponse(results);

          setState((prevState) => ({
            ...prevState,
            duration: results.routes[0].legs[0].duration.text,
            distance: results.routes[0].legs[0].distance.text,
            pickupLocation: results.routes[0].legs[0].start_address,
            dropLocation: results.routes[0].legs[0].end_address,
            start_lat: startLocationLatLng.lat,
            start_lng: startLocationLatLng.lng,
            end_lat: endLocationLatLng.lat,
            end_lng: endLocationLatLng.lng,
            route: route,
          }));
        } catch (e) {
          console.error("Initial route calculation failed:", e);
        }
      };

      calculateInitialRoute();
    }
  }, [isLoaded, paramLeavingFrom, paramGoingTo]);

  const onSubmit = (e) => {
    e.preventDefault();
    const depTime = moment(state.time);

    if (state.differentSeatPricing) {
      const isFrontValid =
        /^\d*\.?\d*$/.test(state.frontSeatPrice) &&
        parseFloat(state.frontSeatPrice) > 0;
      const isBackValid =
        /^\d*\.?\d*$/.test(state.backSeatPrice) &&
        parseFloat(state.backSeatPrice) > 0;

      if (!isFrontValid || !isBackValid) {
        setInvalidTicket(true);
        return;
      }
    } else {
      const isPositiveNumber =
        /^\d*\.?\d*$/.test(state.ticket) &&
        parseFloat(state.ticket) > 0 &&
        parseFloat(state.ticket) <= 99999;

      if (!isPositiveNumber) {
        setInvalidTicket(true);
        return;
      }
    }

    const ticketVal = state.differentSeatPricing
      ? state.backSeatPrice || state.ticket
      : state.ticket;
    const frontPrice = state.differentSeatPricing
      ? parseFloat(state.frontSeatPrice)
      : null;
    const backPrice = state.differentSeatPricing
      ? parseFloat(state.backSeatPrice)
      : null;
    const frontTotal = state.differentSeatPricing
      ? parseInt(state.frontSeatsTotal)
      : state.totalSeats > 1
      ? 1
      : 0;
    const backTotal = Math.max(0, state.totalSeats - frontTotal);

    ridesSubmitFrom({
      ...state,
      ticket: ticketVal,
      front_seat_price: frontPrice,
      back_seat_price: backPrice,
      front_seats_total: frontTotal,
      back_seats_total: backTotal,
      time: depTime,
    });
  };

  const handleInputChange = (event) => {
    setState({
      ...state,
      ticket: event.target.value,
    });
  };

  const handleDateChange = (date) => {
    const initialDate = moment(date);
    const durationToAdd = moment.duration(state.duration);
    const updatedDate = initialDate.add(durationToAdd).toDate();
    setDateError("");
    setState({
      ...state,
      date: date,
      time: null,
      arrivalDate: updatedDate,
    });
  };

  const handleTimeChange = (time) => {
    if (!state.date) {
      setDateError("Select date");
      return;
    }
    const date = new Date(state.date).getDate();
    if (date == new Date().getDate()) {
      if (new Date(time).getTime() < new Date().getTime()) {
        setTimeError("Can not select a past time");
        setState({
          ...state,
          time: null,
        });
        return;
      }
    }
    setTimeError("");
    const initialTime = moment(time);

    // Updated regex to handle optional hours and only minutes
    const regex = /(?:(\d+)\s*hour[s]?)?\s*(\d+)?\s*(min[s]?|minute[s]?)/;
    const match = state.duration.match(regex);

    if (match) {
      const hours = match[1] ? parseInt(match[1], 10) : 0;
      if (hours) {
        initialTime.add(hours, "hours");
      }

      // The minute group is in `match[2]` when hours are present, or `match[1]` when only minutes are present
      const minutes = match[2]
        ? parseInt(match[2], 10)
        : match[1] && !hours
        ? parseInt(match[1], 10)
        : 0;
      if (minutes) {
        initialTime.add(minutes, "minutes");
      }
    }
    setState({
      ...state,
      time: time,
      arrivalTime: initialTime.format("h:mm A"),
    });
  };
  const onMapLoad = (map) => {
    setMap(map);
  };
  const handleOriginChange = () => {
    const details = originRef.current.getPlace();

    try {
      if (details) {
        const addressComponents = details?.address_components;
        const cityObject = addressComponents.find((component) => {
          return component.types.includes("locality");
        });
        if (!cityObject) {
          setState({
            ...state,
            leavingFrom: details.name,
          });
        } else {
          const cityData = cityObject?.long_name;
          setState({
            ...state,
            leavingFrom: cityData,
          });
        }
      }
    } catch (error) {}
  };
  const handleDestinationChange = async () => {
    const details = destinationRef.current.getPlace();

    try {
      if (details) {
        const addressComponents = details?.address_components;
        const cityObject = addressComponents.find((component) => {
          return component.types.includes("locality");
        });

        let cityData;
        if (cityObject) {
          cityData = cityObject?.long_name;
        } else {
          cityData = details.name;
        }

        await calculateRoute(cityData);
      }
    } catch (error) {}
  };

  const calculateRoute = async (cityData) => {
    if (!originRef.current || !destinationRef.current) {
      return;
    }

    const directionService = new google.maps.DirectionsService();
    const results = await directionService.route({
      origin: originRef.current.getPlace().formatted_address,
      destination: destinationRef.current.getPlace().formatted_address,
      travelMode: google.maps.TravelMode.DRIVING,
    });

    const startLocationLatLng = {
      lat: results.routes[0].legs[0].start_location.lat(),
      lng: results.routes[0].legs[0].start_location.lng(),
    };
    const endLocationLatLng = {
      lat: results.routes[0].legs[0].end_location.lat(),
      lng: results.routes[0].legs[0].end_location.lng(),
    };
    const route = results.routes[0].summary;

    setDirectionResponse(results);

    setState({
      ...state,
      duration: results.routes[0].legs[0].duration.text,
      distance: results.routes[0].legs[0].distance.text,
      pickupLocation: originRef.current.getPlace().formatted_address,
      dropLocation: destinationRef.current.getPlace().formatted_address,
      start_lat: startLocationLatLng.lat,
      start_lng: startLocationLatLng.lng,
      end_lat: endLocationLatLng.lat,
      end_lng: endLocationLatLng.lng,
      route: route,
      headingTo: cityData,
    });
  };
  const handleSmokingControl = (e) => {
    setState({
      ...state,
      smoking: currentUser?.gender == "female" ? false : e.target.checked,
    });
  };
  const handleAutomaticApproval = (e) => {
    setState({
      ...state,
      automatic_approval: e.target.checked,
      // female_only: false,
    });
  };
  const handleFemaleOnly = (e) => {
    setState((prevState) => ({
      ...prevState,
      female_only: e.target.checked,
    }));
  };

  const getSeatsByVehicleType = (type) => {
    switch (type) {
      case "car":
        return [1, 2, 3, 4];
      case "van":
        return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14];
      case "mpv":
        return [1, 2, 3, 4, 5, 6];
      case "suv":
        return [1, 2, 3, 4];
      default:
        return [1, 2, 3, 4];
    }
  };

  const handleLicense = () => {
    navigate("/license");
  };

  if (!isLoaded) return <CircularLoader isLoading={isLoaded} />;
  return (
    <MainContainer className="mainContainer">
      <ChildContainer className="childContainer">
        <div className="header">
          <Typography variant="h4" color="primary">
            Create Your Ride
          </Typography>

          <button className="upload-btn" onClick={handleLicense}>
            <FaUpload style={{ marginRight: "8px" }} />
            Upload License
          </button>
        </div>

        <FormContainer>
          <form onSubmit={onSubmit}>
            <Autocomplete
              className="autocomplete-cs"
              options={{
                componentRestrictions: { country: "pk" },
              }}
              onLoad={(autocomplete) => {
                originRef.current = autocomplete;
              }}
              onPlaceChanged={handleOriginChange}
            >
              <input
                className="inputStyle"
                name="address"
                required
                placeholder="F8 Markaz Islamabad"
                type="text"
                ref={originRef}
                defaultValue={paramLeavingFrom}
              />
            </Autocomplete>
            <Autocomplete
              options={{
                componentRestrictions: { country: "pk" },
              }}
              onLoad={(autocomplete) => {
                destinationRef.current = autocomplete;
              }}
              onPlaceChanged={handleDestinationChange}
            >
              <input
                className="inputStyle"
                required
                placeholder="Bahria town, Lahore"
                ref={destinationRef}
                defaultValue={paramGoingTo}
              />
            </Autocomplete>

            <Box mt={1}>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    required={true}
                    label={"Departure date"}
                    date={state.date}
                    handleDateChange={handleDateChange}
                    error={dateError}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TimePicker
                    required={true}
                    label={"Departure time"}
                    time={state.time}
                    handleTimeChange={handleTimeChange}
                    date={state.date}
                    error={timeError}
                  />
                </Grid>
              </Grid>
            </Box>
            <TextField
              style={{ marginTop: "10px" }}
              className="Seats for passengers"
              select
              label="Vehicle Type"
              fullWidth
              variant="outlined"
              value={state.vehicleType}
              onChange={(e) =>
                setState({
                  ...state,
                  vehicleType: e.target.value,
                })
              }
            >
              {["car", "van", "mpv", "suv", "pickup"].map((item, index) => {
                return (
                  <MenuItem key={index + item} value={item}>
                    {item}
                  </MenuItem>
                );
              })}
            </TextField>
            <TextField
              style={{ marginTop: "10px" }}
              className="Seats for passengers"
              select
              label="Seats for passengers"
              fullWidth
              variant="outlined"
              value={state.totalSeats}
              onChange={(e) =>
                setState({
                  ...state,
                  totalSeats: parseInt(e.target.value),
                })
              }
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    style: {
                      maxHeight: 200, // Adjust the height as needed
                    },
                  },
                },
              }}
            >
              {getSeatsByVehicleType(state.vehicleType).map((item, index) => {
                return (
                  <MenuItem key={index + item} value={item}>
                    {item}
                  </MenuItem>
                );
              })}
            </TextField>

            {!state.differentSeatPricing && (
              <Box mt={1}>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <TextField
                      InputProps={{ min: 200 }}
                      placeholder="per passenger ticket e.g 4000"
                      label="Ticket (Standard Seat Price)"
                      type="text"
                      fullWidth
                      variant="outlined"
                      required
                      value={state.ticket}
                      onChange={handleInputChange}
                      error={invalidTicket}
                      helperText={
                        invalidTicket ? "Please enter a valid amount" : ""
                      }
                    />
                  </Grid>
                </Grid>
              </Box>
            )}

            <SeatPricingWrapper>
              <div className="header-toggle">
                <div>
                  <h5>Different Front & Back Seat Pricing</h5>
                  <p>Set specific prices for front and back seats</p>
                </div>
                <CustomSwitch>
                  <input
                    type="checkbox"
                    id="different-seat-pricing-toggle"
                    checked={state.differentSeatPricing}
                    onChange={(e) =>
                      setState({
                        ...state,
                        differentSeatPricing: e.target.checked,
                      })
                    }
                  />
                  <label
                    htmlFor="different-seat-pricing-toggle"
                    className="slider-btn"
                  />
                </CustomSwitch>
              </div>

              {state.differentSeatPricing && (
                <div className="pricing-inputs">
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Front Seat Price (Rs.)"
                        placeholder="e.g. 4500"
                        type="number"
                        fullWidth
                        variant="outlined"
                        required
                        value={state.frontSeatPrice}
                        onChange={(e) =>
                          setState({
                            ...state,
                            frontSeatPrice: e.target.value,
                          })
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Back Seat Price (Rs.)"
                        placeholder="e.g. 4000"
                        type="number"
                        fullWidth
                        variant="outlined"
                        required
                        value={state.backSeatPrice}
                        onChange={(e) =>
                          setState({
                            ...state,
                            backSeatPrice: e.target.value,
                          })
                        }
                      />
                    </Grid>
                  </Grid>

                  <TextField
                    select
                    style={{ marginTop: "10px" }}
                    label="Number of Front Passenger Seats"
                    fullWidth
                    variant="outlined"
                    value={state.frontSeatsTotal}
                    onChange={(e) =>
                      setState({
                        ...state,
                        frontSeatsTotal: parseInt(e.target.value),
                      })
                    }
                  >
                    {Array.from(
                      { length: Math.max(1, state.totalSeats - 1) },
                      (_, i) => i + 1
                    ).map((num) => (
                      <MenuItem key={num} value={num}>
                        {num} Front Seat{num > 1 ? "s" : ""}
                      </MenuItem>
                    ))}
                  </TextField>

                  <div className="preview-card">
                    <div className="seat-badge">
                      <span className="title">💺 Front Seat</span>
                      <span className="price">
                        Rs. {state.frontSeatPrice || 0}
                      </span>
                      <span className="count">
                        {state.frontSeatsTotal} seat(s)
                      </span>
                    </div>
                    <div className="seat-badge">
                      <span className="title">💺 Back Seats</span>
                      <span className="price">
                        Rs. {state.backSeatPrice || 0}
                      </span>
                      <span className="count">
                        {Math.max(
                          0,
                          state.totalSeats - state.frontSeatsTotal
                        )}{" "}
                        seat(s)
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </SeatPricingWrapper>
            <AmenitiesStyled>
              {currentUser?.gender != "female" && (
                <span>
                  <FormControlLabel
                    value={"start"}
                    checked={state.smoking}
                    control={<Switch color="primary" />}
                    label="Allow smoking?"
                    labelPlacement="start"
                    onChange={handleSmokingControl}
                  />
                </span>
              )}
              {!configMerged.featureFlags.freeLaunch && (
                <span className="auto-approval">
                  <FormControlLabel
                    value={"start"}
                    checked={state.automatic_approval}
                    control={<Switch color="primary" />}
                    label="Automaticaly approve requests?"
                    labelPlacement="start"
                    onChange={handleAutomaticApproval}
                  />
                </span>
              )}

            </AmenitiesStyled>

            {currentUser?.gender === "female" && (
              <FemaleOnlyCard $active={state.female_only}>
                <div className="card-header">
                  <div className="title-section">
                    <span className="badge-icon">🎀</span>
                    <div>
                      <h4 className="card-title">Ladies-Only Ride</h4>
                      <p className="card-subtitle">Exclusively for female drivers and passengers</p>
                    </div>
                  </div>
                  <CustomSwitch>
                    <input
                      type="checkbox"
                      id="female-only-toggle"
                      checked={state.female_only}
                      onChange={handleFemaleOnly}
                    />
                    <label htmlFor="female-only-toggle" className="slider-btn" />
                  </CustomSwitch>
                </div>
                
                <div className="card-content">
                  {state.female_only ? (
                    <div className="guidelines">
                      <div className="guideline-item">
                        <span className="check-icon">✓</span>
                        <span>Only verified female passengers can view and book seats on this ride.</span>
                      </div>
                      <div className="guideline-item">
                        <span className="check-icon">✓</span>
                        <span>Ensures a comfortable, secure, and relaxed shared travel environment.</span>
                      </div>
                      <div className="guideline-item">
                        <span className="check-icon">✓</span>
                        <span>Driver contact number is only shared after secure confirmation.</span>
                      </div>
                    </div>
                  ) : (
                    <p className="recommendation-text">
                      💡 <strong>Recommended:</strong> Enabling this option ensures you only receive bookings from verified female passengers for a more secure and comfortable travel experience.
                    </p>
                  )}
                </div>
              </FemaleOnlyCard>
            )}

            {state.automatic_approval && (
              <Alert severity="info" style={{ margin: "0.5rem" }}>
                You acknowledge that your booking requests will be automatically
                confirmed without your approval for this ride.
              </Alert>
            )}
            {state.headingTo && (
              <StyleMapContainer>
                <div className="map">
                  <GoogleMap
                    onLoad={onMapLoad}
                    center={center}
                    zoom={18}
                    options={{
                      zoomControl: false,
                      streetViewControl: false,
                      mapTypeControl: false,
                    }}
                    mapContainerStyle={{ height: "250px", width: "100%" }}
                  >
                    {directionResponse && (
                      <DirectionsRenderer
                        directions={directionResponse}
                        options={{
                          polylineOptions: {
                            strokeWeight: 5, // Set the stroke weight (thickness)
                            strokeColor: COLORS.primaryColor, // Change the route color to red
                          },
                        }}
                      />
                    )}
                  </GoogleMap>
                </div>
              </StyleMapContainer>
            )}

            <BtnContainer>
              <CustomButton
                label={submitting ? "PLEASE WAIT" : "NEXT"}
                type="submit"
              />{" "}
            </BtnContainer>
          </form>
        </FormContainer>
      </ChildContainer>
    </MainContainer>
  );
}

export default Publish;
const MainContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-grow: 1;
  @media ${device.mobileL} {
    flex-direction: column;
  }
`;
const AmenitiesStyled = styled.div`
  display: flex;
  flex-wrap: wrap;
  padding: 10px 0;
  @media (max-width: 546px) {
    font-size: small;
  }
  .auto-approval {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
`;
const ChildContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 1rem;
  justify-content: start;
  padding-right: 60px;
  align-items: center;
  padding: 1rem;
  @media (max-width: 546px) {
    padding: 1rem 1rem 3rem 1rem;
  }
  .header {
    width: 100%;
    max-width: 546px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    h4 {
      @media (max-width: 546px) {
        font-size: 1.5rem;
      }
      @media (max-width: 400px) {
        font-size: 1.2rem;
      }
    }
    .upload-btn {
      border: none;
      padding: 1rem;
      border-radius: 0.5rem;
      background: transparent;
      color: deepskyblue;
      font-weight: 600;
      font-size: 1rem;
      cursor: pointer;
      :hover {
        background: rgb(250, 250, 250);
      }
      @media (max-width: 546px) {
        font-size: 0.8rem;
      }
    }
  }
`;
const StyleMapContainer = styled.div`
  height: 250px;
  width: 100%;
`;

const FormContainer = styled.div`
  border-radius: 6px;
  text-align: center;
  display: flex;
  outline: 0;
  max-width: 768px;

  > form > .MuiTextField-root {
    text-align: center;
    color: white;
    margin-bottom: 5px;
    margin-top: 7px;
    text-align: start;
  }

  > form > .Going {
    text-align: start;
  }
  > form > .datepicker {
    margin-top: 1rem;
    border: "none";
    height: 2rem;
  }
  .inputStyle {
    width: 100%;
    margin: 8px 0;
    padding: 16px 12px;
    font-size: 1rem;
    border: 1px solid lightgray;
    border-radius: 4px;
    :focus {
      outline: 2px solid ${COLORS.primaryColor};
    }
  }
  
}
`;

const BtnContainer = styled.div`
  margin-top: 20px;
  margin-left: auto;
  margin-right: auto;
  width: 100%;
  margin-bottom: 8rem;
  max-width: 350px;
`;

const FemaleOnlyCard = styled.div`
  width: 100%;
  max-width: 546px;
  background: ${(props) =>
    props.$active
      ? "linear-gradient(135deg, rgba(236, 72, 153, 0.06) 0%, rgba(219, 39, 119, 0.02) 100%)"
      : "#ffffff"};
  border: 1px solid ${(props) => (props.$active ? "rgba(236, 72, 153, 0.3)" : "#e2e8f0")};
  border-radius: 16px;
  padding: 1.5rem;
  margin: 1.5rem 0;
  text-align: left;
  box-shadow: ${(props) =>
    props.$active
      ? "0 10px 25px -5px rgba(236, 72, 153, 0.1), 0 8px 10px -6px rgba(236, 72, 153, 0.05)"
      : "0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)"};
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
  }

  .title-section {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .badge-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    width: 44px;
    height: 44px;
    background: ${(props) => (props.$active ? "rgba(236, 72, 153, 0.15)" : "#f1f5f9")};
    border-radius: 12px;
    transition: all 0.3s ease;
  }

  .card-title {
    font-size: 1.1rem;
    font-weight: 700;
    color: ${(props) => (props.$active ? "#be185d" : "#1e293b")};
    margin: 0;
    transition: color 0.3s ease;
  }

  .card-subtitle {
    font-size: 0.85rem;
    color: #64748b;
    margin: 2px 0 0 0;
  }

  .card-content {
    margin-top: 1.2rem;
    border-top: 1px dashed ${(props) => (props.$active ? "rgba(236, 72, 153, 0.2)" : "#e2e8f0")};
    padding-top: 1rem;
    transition: all 0.3s ease;
  }

  .guidelines {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .guideline-item {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    font-size: 0.88rem;
    color: #334155;
    line-height: 1.4;

    .check-icon {
      color: #db2777;
      font-weight: 700;
      background: rgba(236, 72, 153, 0.1);
      border-radius: 50%;
      width: 20px;
      height: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      font-size: 0.75rem;
    }
  }

  .recommendation-text {
    font-size: 0.88rem;
    color: #64748b;
    margin: 0;
    line-height: 1.5;
  }
`;

const CustomSwitch = styled.div`
  position: relative;
  width: 52px;
  height: 28px;

  input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  .slider-btn {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #cbd5e1;
    transition: 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    border-radius: 34px;

    &:before {
      position: absolute;
      content: "";
      height: 20px;
      width: 20px;
      left: 4px;
      bottom: 4px;
      background-color: white;
      transition: 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      border-radius: 50%;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    }
  }

  input:checked + .slider-btn {
    background: linear-gradient(135deg, #ec4899 0%, #db2777 100%);
  }

  input:checked + .slider-btn:before {
    transform: translateX(24px);
  }
`;

const SeatPricingWrapper = styled.div`
  width: 100%;
  margin: 1rem 0;
  padding: 1.25rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  text-align: left;

  .header-toggle {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.75rem;

    h5 {
      margin: 0;
      font-size: 1rem;
      font-weight: 600;
      color: #0f172a;
    }
    p {
      margin: 2px 0 0 0;
      font-size: 0.8rem;
      color: #64748b;
    }
  }

  .pricing-inputs {
    margin-top: 1rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .preview-card {
    margin-top: 1rem;
    padding: 1rem;
    background: #ffffff;
    border-radius: 8px;
    border: 1px dashed ${COLORS.primaryColor};
    display: flex;
    justify-content: space-around;
    align-items: center;

    .seat-badge {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.25rem;

      .title {
        font-size: 0.85rem;
        font-weight: 600;
        color: #334155;
      }
      .price {
        font-size: 1.1rem;
        font-weight: 700;
        color: ${COLORS.primaryColor};
      }
      .count {
        font-size: 0.75rem;
        color: #64748b;
      }
    }
  }
`;

